import React, { useEffect } from 'react';

import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
} from 'lexical';

import { Box } from '../../../../../Box';
import { Icon } from '../../../../../Icon';
import { MenuItem } from '../../../../../MenuItem';
import { Typography } from '../../../../../Typography';

type IndentTypeToBlockName = {
  decrease: string;
  increase: string;
};

const MAX_INDENT = 6;

export const indentTypeToBlockName: IndentTypeToBlockName = {
  decrease: 'Уменьшить отступ',
  increase: 'Увеличить отступ',
};

export type IndentTypeKeys = keyof IndentTypeToBlockName;

export const getIndentIcon = (indent: string) => {
  switch (indent) {
    case 'decrease':
      return (
        <Icon
          name="indent_decrease_outline"
          size="20"
          color="text.light.primary"
        />
      );
    case 'increase':
      return (
        <Icon
          name="indent_increase_outline"
          size="20"
          color="text.light.primary"
        />
      );

    default:
      return (
        <Icon
          name="indent_decrease_outline"
          size="20"
          color="text.light.primary"
        />
      );
  }
};

const CUSTOM_INDENT_CONTENT_COMMAND: LexicalCommand<string> = createCommand(
  'CUSTOM_INDENT_CONTENT_COMMAND',
);

function registerMyCommand(editor: LexicalEditor) {
  return editor.registerCommand(
    CUSTOM_INDENT_CONTENT_COMMAND,
    () => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return false;

      const nodes = selection.getNodes();
      for (const node of nodes) {
        const element = $isElementNode(node) ? node : node.getParent();

        if (!element || !$isElementNode(element)) return false;

        if (element.getIndent && element.setIndent) {
          const indent = element.getIndent();
          if (indent < MAX_INDENT) {
            element.setIndent(indent + 1);
          }
        }
      }

      return true;
    },
    COMMAND_PRIORITY_EDITOR,
  );
}

export const useIndentFormat = (editor: LexicalEditor) => {
  useEffect(() => {
    if (!editor) return;
    const unregister = registerMyCommand(editor);

    return () => {
      unregister();
    };
  }, [editor]);

  const handleIndentFormat = (indent: string) => () => {
    if (indent === 'increase') {
      editor.dispatchCommand(CUSTOM_INDENT_CONTENT_COMMAND, '');
      return;
    }
    if (indent === 'decrease') {
      editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
      return;
    }
  };

  return [
    Object.entries(indentTypeToBlockName).map(([indent, name]) => (
      <MenuItem onClick={handleIndentFormat(indent)} key={indent}>
        <Box display="flex" alignItems="center" gap="8">
          {getIndentIcon(indent)}
          <Typography variant="body3">{name}</Typography>
        </Box>
      </MenuItem>
    )),
    getIndentIcon('decrease'),
  ];
};
